/*
 * DESIGN: Forge & Circuit — Neo-Brutalist Dark Tech
 * Process: Numbered steps with thick left border accent. Mechanical, sequential feel.
 * Like assembly line steps — each one builds on the last.
 */
import Reveal from "./Reveal";

const steps = [
  {
    number: "01",
    title: "Discovery Call",
    description:
      "We start with a 30-minute call to understand your business, your pain points, and where AI can make the biggest impact. No sales pitch — just listening.",
  },
  {
    number: "02",
    title: "Custom AI Roadmap",
    description:
      "We build a tailored strategy document outlining exactly which AI tools and automations will deliver the highest ROI for your specific operation.",
  },
  {
    number: "03",
    title: "Build & Deploy",
    description:
      "Our team builds, tests, and deploys your AI solutions. We handle the tech so you can keep running your business. Typical deployment: 2-6 weeks.",
  },
  {
    number: "04",
    title: "Optimize & Scale",
    description:
      "We monitor performance, fine-tune the AI, and continuously improve. As your business grows, your AI grows with it. Ongoing support included.",
  },
];

export default function Process() {
  return (
    <section className="py-24 md:py-32 relative bg-navy-deep">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric/30 to-transparent" />

      <div className="container">
        {/* Section Header */}
        <div className="mb-16">
          <Reveal
            as="span"
            from="none"
            className="font-mono text-xs text-ember tracking-[0.3em] uppercase block mb-4"
          >
            // HOW IT WORKS
          </Reveal>
          <Reveal
            as="h2"
            from="up"
            duration={0.6}
            className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4"
          >
            From First Call to
            <br />
            <span className="text-electric">Full Deployment</span>
          </Reveal>
          <Reveal
            as="p"
            from="up"
            duration={0.6}
            delay={0.1}
            className="text-steel-light text-lg max-w-2xl"
          >
            We keep it simple. Four steps to transform your business with AI —
            no 12-month contracts, no enterprise complexity.
          </Reveal>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {steps.map((step, i) => (
            <Reveal
              key={i}
              from="up"
              duration={0.5}
              delay={i * 0.1}
              className="relative p-6 md:p-8 border-[3px] border-border bg-card hover:border-electric/40 transition-all group"
            >
              {/* Step number */}
              <div className="flex items-start gap-5">
                <span className="font-mono text-4xl md:text-5xl font-bold text-electric/20 group-hover:text-electric/40 transition-colors leading-none shrink-0">
                  {step.number}
                </span>
                <div>
                  <h3 className="font-heading font-bold text-lg md:text-xl text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-steel-light text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>

              {/* Connecting line for desktop */}
              {i < steps.length - 1 && i % 2 === 0 && (
                <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-[3px] bg-electric/20" />
              )}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
